/**
 * CSV export utility for report data.
 * RFC 4180 escaping; UTF-8 with BOM for Excel compatibility.
 */

const BOM = '\uFEFF';

/**
 * Escape a CSV cell value (commas, quotes, newlines).
 * @param {*} value - Cell value (will be stringified)
 * @returns {string}
 */
function escapeCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Build a CSV string from column definitions and row objects.
 * @param {Array<{ key: string, label: string }>} columnDefs - Column keys and header labels
 * @param {Array<Object>} rows - Array of objects; values taken by key
 * @param {boolean} [includeBom=true] - Prepend UTF-8 BOM for Excel
 * @returns {string}
 */
export function buildCsv(columnDefs, rows, includeBom = true) {
  const headers = columnDefs.map(c => escapeCell(c.label));
  const lines = [headers.join(',')];
  for (const row of rows) {
    const cells = columnDefs.map(c => escapeCell(row[c.key]));
    lines.push(cells.join(','));
  }
  const csv = lines.join('\r\n');
  return includeBom ? BOM + csv : csv;
}

/**
 * Build CSV from raw rows (array of arrays) and optional header row.
 * Useful for multi-section reports (metadata rows + table).
 * @param {Array<Array<string>>} rows - Each row is an array of cell values
 * @param {boolean} [includeBom=true]
 * @returns {string}
 */
export function buildCsvRaw(rows, includeBom = true) {
  const lines = rows.map(row =>
    row.map(cell => escapeCell(cell)).join(',')
  );
  const csv = lines.join('\r\n');
  return includeBom ? BOM + csv : csv;
}

/**
 * Trigger download of a blob file (e.g. ZIP from server).
 */
export function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Trigger download of a CSV file.
 * @param {string} filename - Name without .csv (extension added if missing)
 * @param {string} csvString - Full CSV content (BOM optional)
 */
export function downloadCsv(filename, csvString) {
  const name = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  downloadBlob(name, blob);
}
